/**
 * Modal dialog for submitting user feedback/feature requests.
 */
import { useState } from 'react';
import { Modal, Button, FormSection } from '../ui';

type FeatureRequestModalProps = {
    onClose: () => void;
};

export default function FeatureRequestModal({ onClose }: FeatureRequestModalProps) {
    const [title, setTitle] = useState('');
    const [details, setDetails] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState('');

    /**
     * Submit feature request to server endpoint.
     */
    const handleSubmit = async () => {
        setError('');
        setSuccess('');

        if (!title.trim() || !details.trim()) {
            setError('Title and details are required.');
            return;
        }

        setBusy(true);
        try {
            const apiBase = process.env.REACT_APP_API_BASE_URL || '';
            const res = await fetch(`${apiBase}/api/feature-request`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: title.trim(), details: details.trim() }),
            });

            const text = await res.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch {
                data = {};
            }

            if (!res.ok) {
                setError(data.error || `Server error (${res.status}). Please try again.`);
                return;
            }

            setSuccess('Feature request submitted successfully!');
            setTitle('');
            setDetails('');
            setTimeout(() => onClose(), 1200);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to submit request.';
            setError(message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            title='Feature request'
            onClose={onClose}
            headerVariant='default'
            closeOnOverlayClick={true}
            overlayProps={{ role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'feature-request-modal-title' }}
            headerId='feature-request-modal-title'
        >
            <p className='form-description'>Please frame it as a problem that needs solving.</p>

            <FormSection>
                <label className='form-label'>Title</label>
                <input
                    className='form-input'
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder='Short summary of the problem'
                />
            </FormSection>

            <FormSection>
                <label className='form-label'>Details (required)</label>
                <textarea
                    className='form-textarea form-textarea-h110'
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder='Describe current problem and expected behavior in detail'
                />
            </FormSection>

            {error && <div className='modal-error'>{error}</div>}
            {success && <div className='modal-success'>{success}</div>}

            <div className='modal-actions'>
                <Button variant='secondary' onClick={onClose} disabled={busy}>
                    Cancel
                </Button>
                <Button variant='primary' onClick={handleSubmit} disabled={busy}>
                    {busy ? 'Submitting...' : 'Submit'}
                </Button>
            </div>
        </Modal>
    );
}
